import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { FaCookie, FaTimes, FaCog } from 'react-icons/fa';

const CookieConsent = ({ onOpenPreferences }) => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Controlla se l'utente ha già dato il consenso
    const consent = localStorage.getItem('cosmo-cookie-consent');
    if (!consent) {
      // Mostra il banner dopo un breve delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem('cosmo-cookie-consent', JSON.stringify(consentData));
    
    // Attiva tutti i servizi
    window.gtag && window.gtag('consent', 'update', {
      'analytics_storage': 'granted',
      'ad_storage': 'granted',
      'functionality_storage': 'granted',
      'personalization_storage': 'granted'
    });

    handleClose();
  };

  const handleRejectAll = () => {
    const consentData = {
      essential: true, // I cookie essenziali sono sempre attivi
      analytics: false,
      marketing: false,
      preferences: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem('cosmo-cookie-consent', JSON.stringify(consentData));
    
    // Disattiva tutti i servizi non essenziali
    window.gtag && window.gtag('consent', 'update', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'functionality_storage': 'denied',
      'personalization_storage': 'denied'
    });

    handleClose();
  };

  const handleCustomize = () => {
    onOpenPreferences();
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  if (!isVisible) return null;

  const texts = {
    en: {
      title: "We use cookies",
      description: "We use cookies and similar technologies to enhance your experience on our platform, analyze usage, and provide personalized content. By clicking 'Accept All', you consent to our use of cookies.",
      acceptAll: "Accept All",
      rejectAll: "Reject All",
      customize: "Customize",
      learnMore: "Learn more about our",
      privacyPolicy: "Privacy Policy",
      cookiePolicy: "Cookie Policy"
    },
    it: {
      title: "Utilizziamo i cookie",
      description: "Utilizziamo cookie e tecnologie simili per migliorare la tua esperienza sulla nostra piattaforma, analizzare l'utilizzo e fornire contenuti personalizzati. Cliccando 'Accetta Tutti', acconsenti al nostro uso dei cookie.",
      acceptAll: "Accetta Tutti",
      rejectAll: "Rifiuta Tutti", 
      customize: "Personalizza",
      learnMore: "Scopri di più sulla nostra",
      privacyPolicy: "Privacy Policy",
      cookiePolicy: "Cookie Policy"
    }
  };

  const t = texts[language] || texts.en;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ 
          y: isClosing ? 100 : 0, 
          opacity: isClosing ? 0 : 1 
        }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl"
      >
        <div className="max-w-7xl mx-auto p-4 md:p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            {/* Icon and Content */}
            <div className="flex items-start gap-3 flex-1">
              <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mt-1">
                <FaCookie className="text-amber-600 text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 mb-2">{t.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  {t.description}
                </p>
                <div className="flex flex-wrap gap-1 text-xs text-gray-500">
                  <span>{t.learnMore}</span>
                  <button 
                    onClick={() => {/* Apri Privacy Policy */}}
                    className="text-indigo-600 hover:text-indigo-800 underline"
                  >
                    {t.privacyPolicy}
                  </button>
                  <span>e</span>
                  <button 
                    onClick={() => {/* Apri Cookie Policy */}}
                    className="text-indigo-600 hover:text-indigo-800 underline"
                  >
                    {t.cookiePolicy}
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {t.rejectAll}
              </button>
              <button
                onClick={handleCustomize}
                className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <FaCog className="text-xs" />
                {t.customize}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
              >
                {t.acceptAll}
              </button>
            </div>
          </div>
        </div>

        {/* Subtle animation indicator */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 opacity-20"></div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;